import React from 'react';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { SubsidyRequestsReportResponse } from '@trnsact/trnsact-shared-types';

interface Props {
  report: SubsidyRequestsReportResponse;
  period: string;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatMillions = (value: number): string =>
  value >= 1_000_000 ? `$${(value / 1_000_000).toFixed(2)}M` : formatCurrency(value);

const SummaryCard = ({ children, sx = {} }: { children: React.ReactNode; sx?: object }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const SubsidySummaryCards = ({ report, period }: Props) => {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" fontWeight={600}>
        {period} Summary
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr', gap: 2, mt: 1 }}>
        <SummaryCard sx={{ borderColor: 'success.light', gridRow: 'span 1' }}>
          <Typography variant="caption" color="text.secondary">
            Total Subsidy Paid / Committed
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(report.totalSubsidy)}
          </Typography>
          <Stack direction="row" gap={1} mt={0.5}>
            <Chip
              size="small"
              label={`${formatCurrency(report.contractedDealsTotalSubsidy)} contracted`}
              sx={{ bgcolor: 'success.light', color: 'success.contrastText', fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`${formatCurrency(report.openDealsTotalSubsidy)} open`}
              sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', fontWeight: 600 }}
            />
          </Stack>
        </SummaryCard>

        <SummaryCard>
          <Typography variant="caption" color="text.secondary">
            Total Finance Volume
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatMillions(report.totalFinanceVolume)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Across {report.totalDeals} deals {period}
          </Typography>
        </SummaryCard>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mt: 2 }}>
        <SummaryCard>
          <Typography variant="caption" color="text.secondary">
            Total Deals
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {report.totalDeals}
          </Typography>
          <Stack direction="row" gap={1} mt={0.5}>
            <Chip
              size="small"
              label={`${report.contractedDeals} contracted`}
              sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}
            />
            <Chip
              size="small"
              label={`${report.openDeals} open`}
              sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}
            />
          </Stack>
        </SummaryCard>

        <SummaryCard>
          <Typography variant="caption" color="text.secondary">
            Avg Subsidy %
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {report.avgSubsidyPercentage.toFixed(2)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Range: {report.avgSubsidyPercentageRangeMin}% - {report.avgSubsidyPercentageRangeMax}%
          </Typography>
        </SummaryCard>

        <SummaryCard>
          <Typography variant="caption" color="text.secondary">
            Avg Subsidy Per Deal
          </Typography>
          <Typography variant="h5" fontWeight={700}>
            {formatCurrency(report.avgSubsidyPerContractedDeal)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Contracted deals only
          </Typography>
        </SummaryCard>

        <SummaryCard sx={{ borderColor: 'warning.light' }}>
          <Typography variant="caption" color="text.secondary">
            Open / Pending
          </Typography>
          <Typography variant="h5" fontWeight={700} color="warning.dark">
            {formatCurrency(report.openDealsTotalSubsidy)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {report.openDeals} deals not yet contracted
          </Typography>
        </SummaryCard>
      </Box>

      <Divider sx={{ mt: 3 }} />
    </Box>
  );
};
